import type { BalanceBundle, Guild, TelegramUser } from '@guildrise/shared';
import { useState } from 'react';
import { useTelegramBack } from '../../shared/telegram-back.js';
import { useCollection } from '../collection/use-collection.js';
import { ProfileAvatar } from './profile-avatar.js';
import { ProfileBlocks } from './profile-blocks.js';
import {
  PROFILE_BANNERS,
  PROFILE_FRAME_IDS,
  type ProfileCosmetics,
  readProfileCosmetics,
  saveProfileCosmetics,
} from './profile-cosmetics.js';
import { ProfileDialog } from './profile-dialog.js';
import { ProfileIcon } from './profile-icon.js';
import { profileRank } from './profile-view.js';
import './profile.css';
import './profile-matte.css';
import './profile-glass.css';
import './profile-blocks.css';
import './profile-power.css';

export interface ProfileScreenProps {
  readonly guild: Guild;
  readonly player: TelegramUser;
  readonly frameArt?: string | undefined;
  readonly balance: BalanceBundle;
}

type Editor = 'frame' | 'theme' | 'mail' | 'settings';
const LOCAL_NOTICE = 'Оформление сохраняется на этом устройстве';

export function ProfileScreen(props: ProfileScreenProps): React.ReactElement {
  // Switching accounts also resets an open editor and its unsaved draft.
  return <GuildProfile key={props.guild.id} {...props} />;
}

function GuildProfile({
  guild,
  player,
  frameArt,
  balance,
}: ProfileScreenProps): React.ReactElement {
  const { state: collection, retry: retryCollection } = useCollection(guild.id);
  const [cosmetics, setCosmetics] = useState(() => readProfileCosmetics(guild.id));
  const [draft, setDraft] = useState<ProfileCosmetics>(cosmetics);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [notice, setNotice] = useState(LOCAL_NOTICE);
  const [coverFailed, setCoverFailed] = useState<string | null>(null);
  const playerLabel = player.username
    ? `@${player.username}`
    : [player.firstName, player.lastName].filter(Boolean).join(' ');
  const cancel = () => setEditor(null);
  useTelegramBack(editor === null ? null : cancel);

  const appearance = editor === 'frame' || editor === 'theme' ? draft : cosmetics;
  const activeFrameId = appearance.frameId ?? guild.frameId;
  const activeFrame = balance.frames.find((frame) => frame.frame_id === activeFrameId);
  const activeFrameArt =
    appearance.frameId === null ? (frameArt ?? activeFrame?.art) : activeFrame?.art;
  const banner = PROFILE_BANNERS.find(({ id }) => id === appearance.bannerId) ?? PROFILE_BANNERS[0];
  const frames = PROFILE_FRAME_IDS.flatMap((id) => {
    const frame = balance.frames.find((item) => item.frame_id === id);
    return frame ? [{ ...frame, frame_id: id }] : [];
  });

  function openEditor(next: Editor) {
    setDraft(cosmetics);
    setEditor(next);
  }

  function apply() {
    setCosmetics(draft);
    const saved = saveProfileCosmetics(guild.id, draft);
    setNotice(
      saved
        ? LOCAL_NOTICE
        : 'Не удалось сохранить на устройстве. Выбор действует до выхода из профиля.',
    );
    setEditor(null);
  }

  const avatar = (
    <ProfileAvatar
      name={guild.name}
      logoUrl={guild.logoUrl}
      frameId={activeFrameId}
      frameArt={activeFrameArt}
      animated={appearance.frameMotion}
      size={72}
    />
  );

  return (
    <section
      className="profile-screen"
      aria-label="Профиль гильдии"
      data-motion={appearance.backgroundMotion}
    >
      <header className="profile-header">
        <img
          className="profile-header__cover"
          src={banner.src}
          width={1024}
          height={410}
          alt={banner.alt}
          fetchPriority="high"
          onError={() => setCoverFailed(banner.id)}
          onLoad={() => setCoverFailed(null)}
        />
        <button
          className="profile-header__tool profile-header__edit"
          type="button"
          aria-label="Изменить обложку"
          aria-haspopup="dialog"
          onClick={() => openEditor('theme')}
        >
          <ProfileIcon name="pencil-simple" size={20} />
        </button>
        <div className="profile-header__tools">
          <button
            className="profile-header__tool"
            type="button"
            aria-label="Почта"
            aria-haspopup="dialog"
            onClick={() => openEditor('mail')}
          >
            <ProfileIcon name="envelope-simple" size={20} />
          </button>
          <button
            className="profile-header__tool"
            type="button"
            aria-label="Настройки"
            aria-haspopup="dialog"
            onClick={() => openEditor('settings')}
          >
            <ProfileIcon name="sliders-horizontal" size={20} />
          </button>
        </div>
        <div className="profile-header__identity">
          <button
            className="profile-header__portrait"
            type="button"
            aria-label="Выбрать рамку аватара"
            aria-haspopup="dialog"
            onClick={() => openEditor('frame')}
          >
            {avatar}
          </button>
          <div className="profile-header__copy">
            <div className="profile-header__name">
              <h1 title={guild.name}>{guild.name}</h1>
              <span>#{guild.discriminator}</span>
            </div>
            <p className="profile-header__username" title={playerLabel}>
              {playerLabel}
            </p>
            <div className="profile-header__progress">
              <span>
                Уровень<strong>{guild.level}</strong>
              </span>
              <span className="profile-header__rank">
                Ранг<strong>{profileRank(guild.level)}</strong>
              </span>
            </div>
          </div>
        </div>
      </header>
      <div
        className="profile-screen__below"
        data-background={appearance.backgroundId}
        data-motion={appearance.backgroundMotion}
      >
        <div className="profile-screen__sky" aria-hidden="true">
          <span className="profile-screen__grain" />
        </div>
        {coverFailed === banner.id && (
          <p className="profile-screen__notice" role="alert">
            Не удалось загрузить обложку. Выбери другую через карандаш.
          </p>
        )}
        {notice !== LOCAL_NOTICE && (
          <p className="profile-screen__notice" role="status">
            {notice}
          </p>
        )}
        <ProfileBlocks catalog={balance.characters} collection={collection} />
        {collection.status === 'error' && (
          <div className="profile-screen__notice">
            <p role="alert">{collection.message}</p>
            <button className="btn btn--ghost" type="button" onClick={retryCollection}>
              Повторить
            </button>
          </div>
        )}
      </div>

      {editor === 'frame' && (
        <ProfileDialog title="Рамка аватара" onCancel={cancel} onApply={apply}>
          <div className="profile-editor__preview">
            <div className="profile-editor__avatar">{avatar}</div>
            <div>
              <strong>{activeFrame?.label ?? 'Текущая рамка'}</strong>
            </div>
          </div>
          <fieldset className="profile-editor__frames" aria-label="Рамки">
            {frames.map((frame) => (
              <button
                key={frame.frame_id}
                type="button"
                className="profile-editor__frame"
                aria-pressed={activeFrameId === frame.frame_id}
                onClick={() => setDraft({ ...draft, frameId: frame.frame_id })}
              >
                <span className="profile-editor__frame-stage">
                  <ProfileAvatar
                    name={guild.name}
                    logoUrl={guild.logoUrl}
                    frameId={frame.frame_id}
                    frameArt={frame.art}
                    animated={draft.frameMotion && activeFrameId === frame.frame_id}
                  />
                </span>
                <span>{frame.label}</span>
              </button>
            ))}
          </fieldset>
          <button
            type="button"
            className="profile-editor__reset"
            onClick={() => setDraft({ ...draft, frameId: null })}
          >
            Рамка гильдии
          </button>
          <label className="profile-editor__motion">
            <input
              type="checkbox"
              checked={draft.frameMotion}
              onChange={(event) => setDraft({ ...draft, frameMotion: event.currentTarget.checked })}
            />
            Анимации рамки
          </label>
          <p className="profile-editor__note">
            Системное уменьшение движения отключает анимации. {notice}.
          </p>
        </ProfileDialog>
      )}
      {editor === 'theme' && (
        <ProfileDialog title="Обложка" onCancel={cancel} onApply={apply}>
          <div className="profile-editor__theme-preview">
            <img src={banner.src} width={1024} height={410} alt="Предпросмотр обложки" />
            <span>
              {guild.name} #{guild.discriminator}
            </span>
          </div>
          <div className="profile-editor__themes">
            <h3>Обложка</h3>
            <fieldset className="profile-editor__covers" aria-label="Обложки">
              {PROFILE_BANNERS.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  className="profile-editor__option"
                  aria-pressed={draft.bannerId === option.id}
                  onClick={() => setDraft({ ...draft, bannerId: option.id })}
                >
                  <img src={option.src} width={1024} height={410} alt="" />
                  <span>{option.label}</span>
                </button>
              ))}
            </fieldset>
          </div>
          <label className="profile-editor__motion">
            <input
              type="checkbox"
              checked={draft.backgroundMotion}
              onChange={(event) =>
                setDraft({ ...draft, backgroundMotion: event.currentTarget.checked })
              }
            />
            Анимации панелей
          </label>
          <p className="profile-editor__note">{notice}.</p>
        </ProfileDialog>
      )}
      {(editor === 'mail' || editor === 'settings') && (
        <ProfileDialog title={editor === 'mail' ? 'Почта' : 'Настройки'} onCancel={cancel}>
          <p className="profile-editor__note">
            {editor === 'mail' ? 'Почта пока недоступна.' : 'Настройки пока недоступны.'}
          </p>
          {editor === 'settings' && (
            <p className="profile-editor__note">
              Значки навигации: <a href="https://lucide.dev">Lucide</a> ·{' '}
              <a href="/art/navigation/lucide-LICENSE.txt">ISC / MIT</a>. Цвет и толщина линий
              изменены.
            </p>
          )}
        </ProfileDialog>
      )}
    </section>
  );
}
